"use client";

import React, { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Flame } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { SearchableSelect, type SearchableSelectOption } from "../../components/ui/searchable-select";
import {
  getHotLeads,
  updateHotLeadStatus,
  HOT_LEAD_SHEET_STATUS_OPTIONS,
  type HotLeadRecord,
} from "../../services/approachesapi";
import { getJobsForSelection } from "../../services/jobsapi";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui/tooltip";
import { useRbac } from "../../context";

function companyName(job: HotLeadRecord["jobId"]): string {
  if (!job || typeof job === "string") return "";
  const c = job.companyId;
  if (c && typeof c === "object" && "name" in c) {
    return String((c as { name?: string }).name || "").trim();
  }
  return "";
}

function jobLabel(job: HotLeadRecord["jobId"]): string {
  if (!job || typeof job === "string") return "—";
  const title = job.title?.trim() || "";
  const company = companyName(job);
  if (title && company) return `${title} · ${company}`;
  if (title) return title;
  if (company) return company;
  return "—";
}

/** Tooltip: job title + company name only (no job codes / ids). */
function jobTooltipText(job: HotLeadRecord["jobId"]): string {
  if (!job || typeof job === "string") return job || "—";
  const title = job.title?.trim() || "";
  const company = companyName(job);
  const lines: string[] = [];
  if (title) lines.push(title);
  if (company) lines.push(company);
  return lines.length > 0 ? lines.join("\n") : "—";
}

function jobIdForAddCandidatePayload(jobId: HotLeadRecord["jobId"]): string {
  if (!jobId) return "";
  if (typeof jobId === "object" && jobId !== null && "_id" in jobId) {
    return String((jobId as { _id: string })._id);
  }
  return String(jobId);
}

const ScheduleConfirmModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="relative w-full max-w-xs rounded-xl bg-white p-6 shadow-xl">
        <button
          type="button"
          className="absolute right-3 top-3 text-xl text-gray-400 hover:text-gray-600"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <h2 className="mb-4 text-center text-lg font-semibold">Are you ready for schedule?</h2>
        <p className="mb-4 text-center text-sm text-muted-foreground">
          You will go to Add Candidate to complete the profile, same as on the Approaches page.
        </p>
        <div className="mt-4 flex justify-center gap-3">
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-orange-600 text-white hover:bg-orange-700"
            onClick={onConfirm}
          >
            Yes
          </Button>
        </div>
      </div>
    </div>
  );
};

const HotLeadsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useRbac();
  const isAdmin = user?.role === "ADMIN";
  const tableColSpan = isAdmin ? 7 : 5;
  const [rows, setRows] = useState<HotLeadRecord[]>([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [scheduleConfirmRow, setScheduleConfirmRow] = useState<HotLeadRecord | null>(null);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [jobOptions, setJobOptions] = useState<SearchableSelectOption[]>([
    { value: "all", label: "All Jobs" },
  ]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  const loadJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const response = await getJobsForSelection({ _t: Date.now() });
      const jobs = response?.data?.jobs || response?.data || [];
      const mappedOptions: SearchableSelectOption[] = Array.isArray(jobs)
        ? jobs
            .map((job: any) => {
              const id = job?._id || job?.id || "";
              const title = job?.title || "Untitled Job";
              const company = job?.companyId?.name || job?.companyName || "";
              return {
                value: id,
                label: company ? `${title} - ${company}` : title,
              };
            })
            .filter((opt: SearchableSelectOption) => Boolean(opt.value))
        : [];
      setJobOptions([{ value: "all", label: "All Jobs" }, ...mappedOptions]);
    } catch {
      setJobOptions([{ value: "all", label: "All Jobs" }]);
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getHotLeads({ page, limit, jobId: selectedJobId || undefined });
      setRows(res.data as HotLeadRecord[]);
      setTotalPages(res.totalPages ?? 0);
      setTotalResults(res.totalResults ?? 0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, selectedJobId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadJobs();
  }, [loadJobs]);

  const handleImmediateStatusChange = async (hotLeadId: string, callStatus: string) => {
    if (!callStatus) return;
    setSavingId(hotLeadId);
    try {
      await updateHotLeadStatus(hotLeadId, callStatus);
      await load();
    } finally {
      setSavingId(null);
    }
  };

  const handleScheduleConfirm = () => {
    const hl = scheduleConfirmRow;
    if (!hl) return;
    navigate("/add-candidate", {
      state: {
        fromHotLeads: true,
        existingApproachId: String(hl.approachId),
        approachPayload: {
          name: hl.name,
          phone: hl.phone,
          email: hl.email || "",
          qualifications: hl.qualifications || "",
          source: hl.source,
          callStatus: "Schedule" as const,
          remarks: hl.remarks || "",
          jobId: jobIdForAddCandidatePayload(hl.jobId),
        },
        prefill: {
          dateOfBirth: "",
          skills: [] as string[],
          workExperience: 0,
        },
      },
    });
    setScheduleConfirmRow(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="mx-auto max-w-[1500px] space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-500/15 text-orange-600">
              <Flame className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-semibold tracking-tight md:text-2xl">
                Hot leads
              </h1>
            </div>
          </div>
          <div className="w-full sm:w-80">
            <SearchableSelect
              options={jobOptions}
              value={selectedJobId || "all"}
              onValueChange={(value) => {
                setSelectedJobId(value === "all" ? "" : value);
                setPage(1);
              }}
              placeholder={loadingJobs ? "Loading jobs..." : "All Jobs"}
              emptyText="No jobs found"
              searchPlaceholder="Search jobs..."
              loading={loadingJobs}
              searchable
              className="h-10"
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-lg bg-white shadow-sm">
          <div className="overflow-x-auto">
            <TooltipProvider delayDuration={200}>
            <table className="w-full min-w-[820px] divide-y divide-border text-sm">
              <thead style={{ backgroundColor: "#FFF5F2" }}>
                <tr className="border-b border-gray-200">
                  <th className="bg-[#FFF5F2] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Name
                  </th>
                  <th className="bg-[#FFF5F2] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Phone
                  </th>
                  <th className="bg-[#FFF5F2] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Email
                  </th>
                  <th className="bg-[#FFF5F2] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Job
                  </th>
                  {isAdmin ? (
                    <>
                      <th className="bg-[#FFF5F2] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Action count
                      </th>
                      <th className="bg-[#FFF5F2] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Status
                      </th>
                      <th className="bg-[#FFF5F2] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Actioned by
                      </th>
                    </>
                  ) : (
                    <th className="w-[220px] bg-[#FFF5F2] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Update status
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr className="bg-white">
                    <td colSpan={tableColSpan} className="px-4 py-10 text-center text-gray-600">
                      Loading…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr className="bg-white">
                    <td colSpan={tableColSpan} className="px-4 py-10 text-center text-gray-600">
                      No hot leads right now. They appear when an approach is not reachable
                      or ringing not reachable, and pass your visibility rules.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => (
                    <tr
                      key={r._id}
                      className="border-b border-gray-200 bg-white transition-colors hover:bg-gray-50"
                    >
                      <td className="p-4 font-medium text-gray-800">{r.name}</td>
                      <td className="p-4 tabular-nums text-gray-600">{r.phone}</td>
                      <td className="max-w-[180px] truncate p-4 text-gray-600">
                        {r.email || "—"}
                      </td>
                      <td className="max-w-[220px] p-4 text-gray-600">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="block cursor-default truncate border-b border-dotted border-transparent hover:border-gray-400">
                              {jobLabel(r.jobId)}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent
                            side="top"
                            className="max-w-md border bg-popover px-3 py-2 text-sm shadow-md"
                          >
                            <p className="whitespace-pre-wrap text-left leading-relaxed">
                              {jobTooltipText(r.jobId)}
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </td>
                      {isAdmin && (
                        <>
                          <td className="p-4 text-gray-600">{r.nrRnrSheetActionCount ?? 0}</td>
                          <td className="p-4 text-gray-600">
                            {r.lastSheetActionByUserId ? r.callStatus : "—"}
                          </td>
                          <td className="p-4 text-gray-600">
                            {r.lastSheetActionByUserId?.email ||
                              r.lastSheetActionByUserId?.username ||
                              "—"}
                          </td>
                        </>
                      )}
                      {!isAdmin && (
                        <td className="p-4">
                          <select
                            className="h-9 w-full max-w-[200px] rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-800 focus:ring-2 focus:ring-orange-500/30 disabled:opacity-50"
                            disabled={savingId === r._id}
                            defaultValue=""
                            onChange={(e) => {
                              const v = e.target.value;
                              e.target.selectedIndex = 0;
                              if (!v) return;
                              if (v === "Schedule") {
                                setScheduleConfirmRow(r);
                                return;
                              }
                              void handleImmediateStatusChange(r._id, v);
                            }}
                          >
                            <option value="">Set status…</option>
                            {HOT_LEAD_SHEET_STATUS_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </TooltipProvider>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-gray-600">
              Page {page} of {totalPages} · {totalResults} total
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-gray-300 bg-white hover:bg-[#FFF5F2]"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-gray-300 bg-white hover:bg-[#FFF5F2]"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <ScheduleConfirmModal
        isOpen={!!scheduleConfirmRow}
        onClose={() => setScheduleConfirmRow(null)}
        onConfirm={handleScheduleConfirm}
      />
    </div>
  );
};

export default HotLeadsPage;
